import { Doc } from '@dadohr/shared/domainTypes/document';
import { OktaIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/okta';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Switch,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { enableSso } from '../../../../../../services/orgs';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { removeOktaConfig, useOktaConfig } from './service';

const NAME = 'Okta SSO';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  open,
  onClose,
  config
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  config: Doc<OktaIntegrationConfig>;
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<{ idpMetadata: string }>({
    idpMetadata: ''
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Enable Okta through SAML 2.0 to allow Single-Sign-On for all your employees.'
          )}
        </Alert>

        <FormRow>
          <CustomInputLabel label={'IDP XML Metadata'} fullWidth>
            <TextField
              variant="outlined"
              value={state.idpMetadata}
              multiline
              rows={12}
              onChange={(ev) =>
                setState((s) => ({ ...s, idpMetadata: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>

      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(CF.okta.verifyCredentialsAndStore, {
                  orgId: config.data.orgId,
                  idpMetadata: state.idpMetadata
                })
              }
              disabled={!state.idpMetadata}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const ExistingSetupDialog = ({
  open,
  onClose,
  org,
  config
}: {
  open: boolean;
  onClose: () => void;
  org: IOrganization;
  config: Doc<OktaIntegrationConfig>;
}) => {
  // Say that it's set up. Maybe add a verify button later.
  // No need to show any of the provided xml
  // Add switch to activate/deactivate SSO for the org in general
  const { t } = useUnsafeTranslation();

  const enabledForEmployees =
    org.sso.enabledForEndUsers && org.sso.provider === SsoProviderType.OKTA;
  const enabledForAdmins =
    org.sso.enabledForAdmins && org.sso.provider === SsoProviderType.OKTA;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert
          severity="success"
          icon={false}
          action={
            <ButtonWithPromise
              variant="contained"
              color="primary"
              size="small"
              pending={t('Testing...')}
              onClick={() => {
                return callFirebaseFunction<{ loginUrl: string }>(
                  'adminUser-testSsoLogin',
                  {
                    provider: SsoProviderType.OKTA,
                    orgId: config.data.orgId
                  }
                ).then(({ loginUrl }) => {
                  console.log(loginUrl);
                  window.open(loginUrl, '__blank');
                });
              }}
            >
              {t('Test login')}
            </ButtonWithPromise>
          }
        >
          {t('Successfully integrated OKTA via SAML2.0')}
        </Alert>

        <FormControlLabel
          label={t('Enable Single-Sign On for End Users')}
          control={
            <Switch
              checked={enabledForEmployees}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: !enabledForEmployees,
                  enabledForAdmins: enabledForAdmins,
                  provider: SsoProviderType.OKTA
                });
              }}
              color="primary"
            />
          }
        />

        <FormControlLabel
          label={t('Enable Single-Sign On for Admin Users')}
          control={
            <Switch
              checked={enabledForAdmins}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: enabledForEmployees,
                  enabledForAdmins: !enabledForAdmins,
                  provider: SsoProviderType.OKTA
                });
              }}
              color="primary"
            />
          }
        />
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await enableSso(org.id, {
                enabledForAdmins: false,
                enabledForEndUsers: false
              });
              await removeOktaConfig(org.id);
            }}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const SetupDialog = ({
  config,
  onClose,
  onSuccessfulIntegration: onSucessfulIntegration, // can be ignored since this integration is not an import source
  open
}: IntegrationSetupDialogWithConfigProperties<OktaIntegrationConfig>) => {
  const setup = !!config?.data.idpMetadata;
  const { org } = useCurrentUser();

  return (
    <>
      {config && setup && (
        <ExistingSetupDialog
          org={org}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
      {config && !setup && (
        <InitialSetupDialog
          orgId={org.id}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
    </>
  );
};

export const OktaSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();

  const [config, , error] = useOktaConfig(org.id, userId);
  useErrorLogger('Okta Config error', error);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/okta.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
